import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

const GAME_TYPES = ['coinflip', 'battle', 'blackjack'];

const TYPE_META = {
    coinflip: { icon: '⚡', accent: 'text-cyber-purple border-cyber-purple/60 bg-cyber-purple/10' },
    battle:   { icon: '⚔', accent: 'text-cyber-pink border-cyber-pink/60 bg-cyber-pink/10' },
    blackjack:{ icon: '🃏', accent: 'text-cyber-cyan border-cyber-cyan/60 bg-cyber-cyan/10' },
};

export default function MobileLobbyControls({
    player,
    selectedType,
    setSelectedType,
    bet,
    changeBet,
    createRoom,
    lobbyFilter,
    setLobbyFilter,
    typeCounts,
    className = '',
}) {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);

    const closeModal = () => setOpen(false);

    useEffect(() => {
        if (!open) return;
        const onKey = (e) => { if (e.key === 'Escape') closeModal(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open]);

    const parsedBet = parseInt(bet) || 0;
    const balance = player.balance ?? 0;
    const canCreate = parsedBet > 0 && parsedBet <= balance;

    const onSubmit = (type) => {
        if (!canCreate) return;
        closeModal();
        createRoom(type);
    };

    return (
        <div className={`space-y-3 ${className}`}>
            <button
                onClick={() => setOpen(true)}
                className="neon-btn neon-btn-purple w-full py-3 text-sm tracking-widest"
            >
                {t('game.deployRoom')}
            </button>

            {/* Horizontal filter pills */}
            <div className="flex gap-1.5 overflow-x-auto -mx-1 px-1 pb-1">
                <FilterPill
                    label={t('game.allTypes')}
                    count={typeCounts.all}
                    active={lobbyFilter === 'all'}
                    onClick={() => setLobbyFilter('all')}
                />
                {GAME_TYPES.map((type) => (
                    <FilterPill
                        key={type}
                        label={`${TYPE_META[type].icon} ${t(`game.type.${type}`)}`}
                        count={typeCounts[type] || 0}
                        active={lobbyFilter === type}
                        onClick={() => setLobbyFilter(type)}
                    />
                ))}
            </div>

            {open && createPortal(
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
                    onClick={closeModal}
                >
                    <div
                        className="neon-panel p-4 sm:p-6 max-w-md w-full space-y-5"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <h2 className="text-sm font-display font-bold text-white glow-cyan tracking-widest">
                            {t('game.createRoom')}
                        </h2>

                        <div>
                            <label className="neon-label block mb-2">{t('game.wagerAmount')}</label>
                            <input
                                onChange={changeBet}
                                value={bet || ''}
                                type="number"
                                min={1}
                                max={balance}
                                autoFocus
                                placeholder={t('game.wagerPlaceholder')}
                                className="neon-input w-full"
                            />
                            <div className="text-[10px] text-cyber-dim tracking-widest mt-1.5 flex justify-between">
                                <span>{t('game.availableLabel')}</span>
                                <span className="text-cyber-purple glow-purple font-mono">{balance}¢</span>
                            </div>
                        </div>

                        <div>
                            <div className="neon-label mb-2 text-center">{t('game.selectGameType')}</div>
                            <div className="grid grid-cols-3 gap-2">
                                {GAME_TYPES.map((type) => {
                                    const meta = TYPE_META[type] || TYPE_META.coinflip;
                                    const active = selectedType === type;
                                    return (
                                        <button
                                            key={type}
                                            type="button"
                                            onClick={() => setSelectedType(type)}
                                            className={`flex flex-col items-center gap-1 py-3 px-2 rounded-md border transition-all duration-200 ${
                                                active ? meta.accent : 'border-cyber-border text-cyber-dim hover:text-cyber-text'
                                            }`}
                                        >
                                            <span className="text-2xl">{meta.icon}</span>
                                            <span className="text-[10px] font-display tracking-widest uppercase">
                                                {t(`game.type.${type}`)}
                                            </span>
                                        </button>
                                    );
                                })}
                            </div>
                            {!canCreate && (
                                <div className="text-[10px] text-cyber-pink tracking-widest text-center mt-2">
                                    {parsedBet === 0
                                        ? t('notify.bet_too_low')
                                        : t('notify.not_enough_money')}
                                </div>
                            )}
                        </div>

                        <div className="space-y-2">
                            <button
                                type="button"
                                onClick={() => onSubmit(selectedType)}
                                disabled={!canCreate}
                                className={`neon-btn neon-btn-purple w-full py-2.5 text-sm tracking-widest ${
                                    !canCreate ? 'opacity-40 cursor-not-allowed' : ''
                                }`}
                            >
                                {t('game.deployRoom')}
                            </button>
                            <button onClick={closeModal} className="neon-btn w-full text-xs opacity-70">
                                {t('common.cancel')}
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
}

function FilterPill({ label, count, active, onClick }) {
    return (
        <button
            type="button"
            onClick={onClick}
            className={`shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-md border text-[10px] font-display tracking-widest uppercase transition-all duration-200 whitespace-nowrap ${
                active
                    ? 'border-cyber-cyan/60 text-cyber-cyan bg-cyber-cyan/10'
                    : 'border-cyber-border text-cyber-dim hover:text-cyber-text'
            }`}
        >
            <span>{label}</span>
            <span className="font-mono opacity-80">[{count}]</span>
        </button>
    );
}
